import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Tag, Save, RefreshCw, Search, Check, Info, Zap } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PROFILS = [
  { key: 'particulier', label: 'Particulier', color: '#9CA3AF', note: 'Prix public affichu00e9 sur le site' },
  { key: 'entreprise', label: 'Entreprise', color: 'var(--admin-teal)', note: '-15% standard' },
  { key: 'association', label: 'Association', color: 'var(--admin-blue)', note: '-15% standard' },
  { key: 'ce', label: 'CE', color: 'var(--admin-amber)', note: '-10% standard' },
  { key: 'groupe', label: 'Partenaire', color: 'var(--admin-gold)', note: '-20% standard' },
];

export default function AdminTarifs() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [edited, setEdited] = useState({});  // { productId: { profil: value } }
  const [applyingGrid, setApplyingGrid] = useState(false);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/tarifs`, { headers: H });
      const data = await res.json();
      setProducts(data.products || []);
      setEdited({});
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleEdit(productId, profil, value) {
    setEdited(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [profil]: value }
    }));
  }

  function getVal(product, profil) {
    return edited[product.id]?.[profil] !== undefined
      ? edited[product.id][profil]
      : (product.tarifs?.[profil] ?? '');
  }

  async function saveTarifs(product) {
    setSaving(prev => ({ ...prev, [product.id]: true }));
    try {
      const localEdits = edited[product.id] || {};
      const body = { product_id: product.id };
      PROFILS.forEach(p => {
        const v = localEdits[p.key];
        if (v !== undefined && v !== '') body[p.key] = parseFloat(v);
      });
      const res = await fetch(`${BACKEND}/api/admin/tarifs/${product.id}`, {
        method: 'PUT', headers: H, body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error();
      toast.success(`Tarifs mis u00e0 jour : ${product.nom}`);
      // Rafraichir ce produit
      const updated = await res.json();
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, tarifs: updated.tarifs } : p));
      setEdited(prev => { const n = { ...prev }; delete n[product.id]; return n; });
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(prev => ({ ...prev, [product.id]: false }));
  }

  async function applyStandardGrid() {
    if (!window.confirm('Appliquer la grille standard (Entreprise -15%, Asso -15%, CE -10%, Partenaire -20%) u00e0 tous les produits avec un prix public defini ?')) return;
    setApplyingGrid(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/tarifs/appliquer-grille`, {
        method: 'POST', headers: H,
        body: JSON.stringify({ grille: { entreprise: 15, association: 15, ce: 10, groupe: 20 } })
      });
      const data = await res.json();
      toast.success(data.message);
      load();
    } catch { toast.error('Erreur'); }
    setApplyingGrid(false);
  }

  function autoCalc(product, baseVal) {
    const base = parseFloat(baseVal);
    if (isNaN(base) || base === 0) return;
    setEdited(prev => ({
      ...prev,
      [product.id]: {
        particulier: base,
        entreprise: parseFloat((base * 0.85).toFixed(2)),
        association: parseFloat((base * 0.85).toFixed(2)),
        ce: parseFloat((base * 0.90).toFixed(2)),
        groupe: parseFloat((base * 0.80).toFixed(2)),
      }
    }));
  }

  const filtered = products.filter(p =>
    !search || p.nom.toLowerCase().includes(search.toLowerCase())
  );
  const hasEdits = Object.keys(edited).length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }} data-testid="admin-tarifs">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Parametres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Tarifs par profil</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Grilles tarifaires</p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-text)' }}>Tarifs par profil</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Modifiez les prix pour chaque profil client</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={applyStandardGrid} disabled={applyingGrid} size="sm"
            style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Zap size={14} className="mr-1" /> Appliquer grille standard
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'var(--admin-teal-dim)', border: '1px solid var(--admin-teal-line)' }}>
        <Info size={14} style={{ color: 'var(--admin-teal)' }} />
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
          Le tarif <strong style={{ color: 'var(--admin-text)' }}>Particulier</strong> est le prix public affichu00e9 sur le site.
          Les autres profils s'appliquent aux clients connectu00e9s selon leur type de compte.
        </p>
      </div>

      {/* Profils legend */}
      <div className="flex flex-wrap gap-2">
        {PROFILS.map(p => (
          <span key={p.key} className="text-xs px-2.5 py-1 rounded-full font-semibold"
            style={{ background: `${p.color}15`, color: p.color }}>
            {p.label} — {p.note}
          </span>
        ))}
      </div>

      {/* Recherche */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }} />
        <Input placeholder="Rechercher un produit..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9" style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const isEditing = !!edited[p.id];
            const isSur = !p.tarifs?.particulier || p.tarifs.particulier === 0;
            return (
              <div key={p.id} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: isEditing ? '1px solid rgba(212,175,55,0.3)' : '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {/* Nom */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {p.images?.[0] && <img src={p.images[0]} alt={p.nom} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{p.type}</p>
                    </div>
                  </div>

                  {isSur ? (
                    <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--admin-muted)' }}>Sur devis</span>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      {PROFILS.map(prof => (
                        <div key={prof.key} className="flex flex-col items-center">
                          <label className="text-xs mb-1" style={{ color: prof.color }}>{prof.label}</label>
                          <div className="flex items-center">
                            <input
                              type="number" step="0.5" min="0"
                              value={getVal(p, prof.key)}
                              onChange={e => handleEdit(p.id, prof.key, e.target.value)}
                              onFocus={prof.key === 'particulier' ? undefined : undefined}
                              className="w-20 text-sm text-center px-2 py-1.5 rounded-lg"
                              style={{ background: 'var(--admin-surface2)', border: `1px solid ${prof.color}30`, color: 'var(--admin-text)', outline: 'none' }}
                            />
                            <span className="ml-1 text-xs" style={{ color: 'var(--admin-faint)' }}>€</span>
                          </div>
                          {prof.key === 'particulier' && edited[p.id]?.particulier && (
                            <button onClick={() => autoCalc(p, edited[p.id].particulier)}
                              className="text-xs mt-0.5" style={{ color: 'var(--admin-gold)' }}>Auto u2192</button>
                          )}
                        </div>
                      ))}
                      <Button onClick={() => saveTarifs(p)} disabled={saving[p.id] || !isEditing} size="sm"
                        style={{ background: isEditing ? 'rgba(31,157,122,0.2)' : 'rgba(255,255,255,0.04)', color: isEditing ? '#1F9D7A' : 'rgba(255,255,255,0.3)' }}>
                        {saving[p.id] ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasEdits && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 px-4 py-3 rounded-xl"
          style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.3)' }}>
          <Tag size={14} style={{ color: 'var(--admin-gold)' }} />
          <span className="text-sm" style={{ color: 'var(--admin-text)' }}>Modifications non sauvegardues</span>
        </div>
      )}
    </div>
  );
}
